import { Autocomplete, Box, Button, Checkbox, IconButton, MenuItem, Select, TextField, Typography, useTheme } from "@mui/material";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useMemo, useState } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import { v4 as uuidv4 } from 'uuid';
import { getRuleByPolicyDiagram } from "../../../../utils/convertPolicyStructure";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ConfirmDialogReuse from "../../ReuseComponents/ConfirmDialogReuse";
import SettingsIcon from '@mui/icons-material/Settings';
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { getClients } from "../../../../redux/clientsSlice";
import { IPInputCell } from "./IPInputCell";
import { getOpenSimulatorPolicy } from "../../../../redux/layoutControlSlice";
import { getSelectedProfileSimulate } from "../../../../redux/simulateMgtSlice";
dayjs.extend(customParseFormat);
// import { DateTimePicker } from "@mui/x-date-pickers-pro";
// import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

// const initialRows = [
//     { id: "g-app", kind: "group", checked: true, resource: "Applications" },
//     {
//         id: "app-1",
//         kind: "item",
//         checked: true,
//         resource: "trb-watch",
//         type: "Web",
//         action: "OPEN",
//         result: "Permit",
//     },
//     {
//         id: "app-2",
//         kind: "item",
//         checked: true,
//         resource: "trb-eoc",
//         type: "Web",
//         action: "OPEN",
//         ip_address: "10.226.39.128",
//         date_time: "2026-06-20 08:00:00",
//         result: "Permit",
//     },

//     { id: "g-api", kind: "group", checked: true, resource: "RESTful-API" },
//     {
//         id: "api-1",
//         kind: "item",
//         checked: true,
//         resource: "iaamreg-api",
//         type: "API",
//         action: "GET",
//         param: "/iaam-api/applications",
//         environment: "trb-report",
//         environment_type: 'Web',
//         result: "Denied",
//     },
//     {
//         id: "api-2",
//         kind: "item",
//         checked: true,
//         resource: "iaamreg-api",
//         type: "API",
//         action: "GET",
//         param: "/iaam-api/applications",
//         environment: "trb-watch",
//         environment_type: 'Web',
//         result: "Permit",
//     },
//     {
//         id: "api-3",
//         kind: "item",
//         checked: true,
//         resource: "iaamreg-api",
//         type: "API",
//         action: "GET",
//         param: "/iaam-api/applications",
//         environment: "trb-eoc",
//         environment_type: 'Web',
//         result: "Permit",
//     },

//     { id: "g-data", kind: "group", checked: true, resource: "Data" },
// ];

export default function DataGridPolicySimulator(props) {
    const { rows, setRows } = props;
    const theme = useTheme();
    // const apiRef = useGridApiRef();

    const groupTypeMap = {
        Applications: "WEB",
        "RESTful-API": "API",
        Data: "DATA",
    };

    const openSimulatorPolicy = useSelector(getOpenSimulatorPolicy);
    const clients = useSelector(getClients);
    const selectedProfileSimulate = useSelector(getSelectedProfileSimulate);

    const [resourceOptions, setResourceOptions] = useState([]);
    const [incomingResourceOptions, setIncomingResourceOptions] = useState([]);
    const [openConfirmRemove, setOpenConfirmRemove] = useState(false);
    const [rowRemove, setRowRemove] = useState(null);
    const [disabled, setDisabled] = useState(false);

    const persistRows = useCallback((nextRows) => {
        const policyRequest = JSON.parse(localStorage.getItem('policy-simulator') || "{}");
        const policyFile = JSON.parse(localStorage.getItem('policy-file') || "{}");
        const policyDiagramId = policyFile?.policy_diagram_id;
        const profileId = selectedProfileSimulate?.id;

        if (!policyDiagramId || !profileId) return;

        const policy_request = { ...policyRequest };
        const rawDiagramStore = policy_request[policyDiagramId];
        const diagramStore = rawDiagramStore && !Array.isArray(rawDiagramStore) && typeof rawDiagramStore === "object"
            ? { ...rawDiagramStore }
            : {};

        const rawProfileStore = diagramStore[profileId];
        const profileStore = rawProfileStore && !Array.isArray(rawProfileStore) && typeof rawProfileStore === "object"
            ? { ...rawProfileStore }
            : {};

        profileStore.rows = nextRows;
        diagramStore[profileId] = profileStore;
        policy_request[policyDiagramId] = diagramStore;

        localStorage.setItem('policy-simulator', JSON.stringify(policy_request));
    }, [selectedProfileSimulate]);

    const validateIPv4 = (ip) => {
        if (!ip) return false;

        const parts = ip.split(".");

        if (parts.length !== 4) return false;

        return parts.every((part) => {
            if (!/^\d+$/.test(part)) return false;

            const num = Number(part);

            return num >= 0 && num <= 255;
        });
    };

    const validateOptionalIPv4 = useCallback((ip) => {
        const normalizedIP = typeof ip === "string" ? ip.trim() : "";
        if (!normalizedIP) return true;
        return validateIPv4(normalizedIP);
    }, []);

    const validateOptionalDateTime = useCallback((value) => {
        if (value === null || value === undefined || value === "") {
            return true;
        }

        if (dayjs.isDayjs(value)) {
            return value.isValid();
        }

        return dayjs(String(value), "YYYY-MM-DD HH:mm:ss", true).isValid();
    }, []);

    const isValidRowByType = useCallback((row) => {
        if (row?.kind !== "item") return true;

        const resourceValid = Boolean(String(row.resource || "").trim());
        const actionValid = Boolean(String(row.action || "").trim());
        const paramValid = Boolean(String(row.param || "").trim());
        const ipValid = validateOptionalIPv4(row.ip_address);
        const dateTimeValid = validateOptionalDateTime(row.date_time);

        if (row.type === "WEB") {
            return resourceValid && ipValid && dateTimeValid;
        }

        if (row.type === "API") {
            return resourceValid && actionValid && paramValid && ipValid && dateTimeValid;
        }

        if (row.type === "DATA") {
            return resourceValid && actionValid && ipValid && dateTimeValid;
        }

        return false;
    }, [validateOptionalIPv4, validateOptionalDateTime]);

    const normalizeRowsWithValidation = useCallback((sourceRows) => {
        const safeRows = Array.isArray(sourceRows) ? sourceRows : [];

        const normalizedRows = safeRows.map((row) => {
            if (row?.kind !== "item") {
                if (row?.disabled_checked === false) {
                    return row;
                }
                return { ...row, disabled_checked: false };
            }

            const disabledChecked = !isValidRowByType(row);
            return {
                ...row,
                disabled_checked: disabledChecked,
                checked: disabledChecked ? false : Boolean(row.checked),
            };
        });

        const hasCheckedItemByType = {
            WEB: normalizedRows.some((row) => row.kind === "item" && row.type === "WEB" && Boolean(row.checked)),
            API: normalizedRows.some((row) => row.kind === "item" && row.type === "API" && Boolean(row.checked)),
            DATA: normalizedRows.some((row) => row.kind === "item" && row.type === "DATA" && Boolean(row.checked)),
        };

        return normalizedRows.map((row) => {
            if (row?.kind !== "group") return row;

            const groupType = groupTypeMap[row.resource];
            if (!groupType) return row;

            if (!hasCheckedItemByType[groupType] && row.checked) {
                return {
                    ...row,
                    checked: false,
                };
            }

            return row;
        });
    }, [isValidRowByType]);

    const updateRow = useCallback((id, patch) => {
        const safeRows = Array.isArray(rows) ? rows : [];
        const shouldResetResult = Object.keys(patch || {}).some((key) => key !== "checked"); // ไม่นำ checked มาพิจารณาในการ reset result

        const updatedRows = normalizeRowsWithValidation(safeRows.map(row =>
            row.id === id
                ? {
                    ...row,
                    ...patch,
                    ...(shouldResetResult ? { result: "" } : {}),
                }
                : row
        ));

        persistRows(updatedRows);
        setRows(updatedRows);
    }, [rows, normalizeRowsWithValidation, persistRows, setRows]);

    const isDefaultItemRow = (row) => row?.kind === "item" && Boolean(row?.is_default_item);

    const handleToggleChecked = useCallback((targetRow, checkedValue) => {
        const safeRows = Array.isArray(rows) ? rows : [];

        if (targetRow?.kind !== "group") {
            updateRow(targetRow.id, { checked: checkedValue });
            return;
        }

        const targetType = groupTypeMap[targetRow.resource];
        if (!targetType) {
            updateRow(targetRow.id, { checked: checkedValue });
            return;
        }

        const updatedRows = normalizeRowsWithValidation(
            safeRows.map((row) => {
                if (row.id === targetRow.id) {
                    return {
                        ...row,
                        checked: checkedValue,
                    };
                }

                if (row.kind === "item" && row.type === targetType) {
                    return {
                        ...row,
                        checked: checkedValue,
                    };
                }

                return row;
            })
        );

        persistRows(updatedRows);
        setRows(updatedRows);
    }, [rows, updateRow, normalizeRowsWithValidation, persistRows, setRows]);

    const createEmptyItemRowByType = useCallback((type) => {
        const actionDefault = type === "WEB" ? "OPEN" : "";
        return {
            id: uuidv4(),
            kind: "item",
            checked: false,
            is_default_item: false,
            resource: "",
            type: type,
            action: actionDefault,
            param: "",
            environment: "",
            environment_type: "",
            ip_address: "",
            date_time: null,
            result: "",
            disabled_checked: true,
        };
    }, []);

    const handleAddRowToGroup = useCallback((groupRow) => {
        const safeRows = Array.isArray(rows) ? rows : [];
        const groupName = groupRow?.resource;
        const type = groupTypeMap[groupName];

        if (!type) return;

        const groupIndex = safeRows.findIndex(
            (row) => row.kind === "group" && row.resource === groupName
        );

        if (groupIndex === -1) return;

        let insertIndex = safeRows.length;
        for (let i = groupIndex + 1; i < safeRows.length; i += 1) {
            if (safeRows[i].kind === "group") {
                insertIndex = i;
                break;
            }
        }

        const newRow = createEmptyItemRowByType(type);
        const updatedRows = normalizeRowsWithValidation([
            ...safeRows.slice(0, insertIndex),
            newRow,
            ...safeRows.slice(insertIndex),
        ]);

        persistRows(updatedRows);
        setRows(updatedRows);
    }, [rows, createEmptyItemRowByType, normalizeRowsWithValidation, persistRows, setRows]);

    const stopGridEvent = (e) => {
        e.stopPropagation();
    };

    const handleRemove = (row) => {
        setRowRemove(row);
        setOpenConfirmRemove(true);
    };

    const handleCancelRemove = () => {
        setOpenConfirmRemove(false);
        setRowRemove(null);
    };

    const onRemove = () => {
        if (!rowRemove.id) return;
        const removed = normalizeRowsWithValidation(rows.filter(r => r.id !== rowRemove.id));
        persistRows(removed);
        setRows(removed);
        setOpenConfirmRemove(false);
    };

    const filterResourceOptions = (type) => {
        return resourceOptions.filter(option => option.type === type)
    };

    const filterIncomingResourceOptions = (type) => {
        if (type === "API") {
            return incomingResourceOptions.filter(option => option.type === "WEB")
        } else if (type === "DATA") {
            return incomingResourceOptions.filter(option => (option.type === "WEB" || option.type === "API"))
        }
        return [];
    };

    const columns = useMemo(
        () => [
            {
                field: "checked",
                headerName: "",
                width: 20,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    const isItemRow = params.row.kind === "item";
                    const disabledChecked = Boolean(params.row.disabled_checked);

                    return (
                        <Checkbox
                            size="small"
                            checked={Boolean(params.row.checked)}
                            disabled={isItemRow && disabledChecked}
                            onMouseDown={stopGridEvent}
                            onChange={(e) => {
                                if (isItemRow && disabledChecked) return;
                                handleToggleChecked(params.row, e.target.checked)
                            }
                            }
                        />
                    );
                },
            },
            {
                field: "resource",
                headerName: "Accessed Resource",
                width: 180,
                sortable: false,
                renderCell: (params) => {

                    if (params.row.kind === "group") {
                        return (
                            <Typography fontSize={12} fontWeight={600}>
                                {params.row.resource}
                            </Typography>
                        );
                    }

                    if (params.row.kind === "item") {
                        if (isDefaultItemRow(params.row)) {
                            return (
                                <Typography fontSize={12} noWrap>
                                    {params.row.resource || "-"}
                                </Typography>
                            );
                        }

                        const options = filterResourceOptions(params.row.type);
                        const resourceValue = options.some(opt => opt.value === params.row.resource)
                            ? params.row.resource
                            : "";
                        return (
                            <Select
                                size="small"
                                value={resourceValue}
                                onChange={(e) => {
                                    const selectedOption = options.find((opt) => opt.value === e.target.value);
                                    updateRow(params.row.id, {
                                        resource: e.target.value,
                                        type: selectedOption?.type || params.row.type,
                                    });
                                }}
                                onMouseDown={stopGridEvent}
                                onClick={stopGridEvent}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected) return "-";

                                    const text = String(selected);
                                    const maxLength = 18; // ปรับความยาวได้

                                    return text.length > maxLength
                                        ? text.substring(0, maxLength) + "..."
                                        : text;
                                }}
                                sx={{
                                    width: "100%",
                                    height: 25,
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",

                                    "& .MuiSelect-select": {
                                        padding: "2px 8px",
                                        display: "flex",
                                        alignItems: "center",
                                    },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            "& .MuiMenuItem-root": {
                                                fontSize: 12,
                                                minHeight: 30,
                                            },
                                        },
                                    },
                                }}
                            >
                                {options.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        );
                    }

                    return (
                        <Typography fontSize={12} noWrap>
                            {params.row.resource}
                        </Typography>
                    );
                },
            },
            {
                field: "action",
                headerName: "Action",
                width: 100,
                sortable: false,
                renderCell: (params) => {
                    if (params.row.kind === "item") {
                        if (isDefaultItemRow(params.row) && (params.row.type === "API" || params.row.type === "DATA")) {
                            return (
                                <Typography fontSize={12}>{params.row.action || "-"}</Typography>
                            )
                        }

                        if (params.row.type === "WEB" || params.row.type === "NATIVE") {
                            return (
                                <Typography fontSize={12}>{"-"}</Typography>
                            )
                        } else if (params.row.type === "API") {
                            return (
                                <Select
                                    size="small"
                                    value={params.row.action ?? ""}
                                    onMouseDown={stopGridEvent}
                                    onChange={(e) =>
                                        updateRow(params.row.id, { action: e.target.value })
                                    }
                                    className="tinySelect"
                                    displayEmpty
                                    renderValue={(selected) => selected || "-"}
                                    sx={{
                                        width: "100%",
                                        minWidth: 85,
                                    }}
                                >
                                    <MenuItem value="GET">GET</MenuItem>
                                    <MenuItem value="POST">POST</MenuItem>
                                    <MenuItem value="PUT">PUT</MenuItem>
                                    <MenuItem value="DELETE">DELETE</MenuItem>
                                </Select>
                            )
                        } else {
                            return (
                                <Select
                                    size="small"
                                    value={params.row.action ?? ""}
                                    onMouseDown={stopGridEvent}
                                    onChange={(e) =>
                                        updateRow(params.row.id, { action: e.target.value })
                                    }
                                    className="tinySelect"
                                    displayEmpty
                                    renderValue={(selected) => selected || "-"}
                                    sx={{
                                        width: "100%",
                                        minWidth: 85,
                                    }}
                                >
                                    <MenuItem value="SELECT">SELECT</MenuItem>
                                    <MenuItem value="INSERT">INSERT</MenuItem>
                                    <MenuItem value="UPDATE">UPDATE</MenuItem>
                                    <MenuItem value="DELETE">DELETE</MenuItem>
                                </Select>
                            )
                        }
                    } else {
                        return null;
                    }
                }
            },
            {
                field: "param",
                headerName: "Param",
                width: 200,
                sortable: false,
                renderCell: (params) => {
                    if (params?.row?.kind === "item" && params?.row?.type === "API") {
                        if (isDefaultItemRow(params.row)) {
                            return (
                                <Typography fontSize={12}>{params.row.param || "-"}</Typography>
                            )
                        }

                        return (
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.75}
                                onMouseDown={stopGridEvent}
                            >
                                <Typography fontSize={12}>path:</Typography>
                                <TextField
                                    size="small"
                                    value={params.row.param ?? ""}
                                    onChange={(e) =>
                                        updateRow(params.row.id, { param: e.target.value })
                                    }
                                    className="tinyInput"
                                />
                            </Box>
                        )
                    } else if (params?.row?.kind === "item" && !params?.row?.param) {
                        return (
                            <Typography fontSize={12}>-</Typography>
                        )
                    } else {
                        return (
                            <Typography fontSize={12}></Typography>
                        )
                    }
                }
            },
            {
                field: "environment",
                headerName: "Incoming Resource",
                width: 180,
                sortable: false,
                renderCell: (params) => {
                    const options = filterIncomingResourceOptions(params.row.type);
                    const environmentValue = options.some(opt => opt.value === params.row.environment)
                        ? params.row.environment
                        : "";

                    if (params.row.kind === "item" && (params.row.type === "API" || params.row.type === "DATA")) {
                        if (isDefaultItemRow(params.row)) {
                            return (
                                <Typography fontSize={12} noWrap>
                                    {params.row.environment || "-"}
                                </Typography>
                            )
                        }

                        return (
                            <Select
                                size="small"
                                value={environmentValue}
                                onChange={(e) => {
                                    const selectedOption = options.find((opt) => opt.value === e.target.value);
                                    updateRow(params.row.id, {
                                        environment: e.target.value,
                                        environment_type: selectedOption?.type || "",
                                    });
                                }}
                                onMouseDown={stopGridEvent}
                                onClick={stopGridEvent}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected) return "-";

                                    const text = String(selected);
                                    const maxLength = 18; // ปรับความยาวได้

                                    return text.length > maxLength
                                        ? text.substring(0, maxLength) + "..."
                                        : text;
                                }}
                                sx={{
                                    width: "100%",
                                    height: 25,
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",

                                    "& .MuiSelect-select": {
                                        padding: "2px 8px",
                                        display: "flex",
                                        alignItems: "center",
                                    },
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            "& .MuiMenuItem-root": {
                                                fontSize: 12,
                                                minHeight: 30,
                                            },
                                        },
                                    },
                                }}
                            >
                                {options.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        )
                    }
                    if (params.row.kind === "item") {
                        return (
                            <Typography fontSize={12}>-</Typography>
                        )
                    } else {
                        return (
                            <Typography fontSize={12}></Typography>
                        )
                    }
                }
            },
            {
                field: "ip_address",
                headerName: "Incoming IP Address",
                width: 150,
                sortable: false,
                align: "center",
                headerAlign: "center",
                renderHeader: () => {
                    return (
                        <Typography
                            fontSize={12}
                            textAlign="center"
                            lineHeight={1.2}
                        >
                            Incoming <br />
                            IP Address
                        </Typography>
                    );
                },
                renderCell: (params) => {
                    if (params.row.kind !== "group") {
                        if (isDefaultItemRow(params.row)) {
                            return (
                                <Typography fontSize={12}>{params.row.ip_address || "-"}</Typography>
                            );
                        }

                        return (
                            <IPInputCell
                                value={params.row.ip_address}
                                onChange={(newIP) => {
                                    updateRow(params.row.id, {
                                        ip_address: newIP,
                                    });
                                }}
                            />
                        );
                    }

                    return null;
                }
            },
            {
                field: "date_time",
                headerName: "Incoming Date Time",
                width: 200,
                sortable: false,
                align: "center",
                headerAlign: "center",
                renderHeader: () => {
                    return (
                        <Typography
                            fontSize={12}
                            textAlign="center"
                            lineHeight={1.2}
                        >
                            Incoming <br />
                            Date Time
                        </Typography>
                    );
                },
                renderCell: (params) => {
                    const { row } = params;

                    if (row.kind !== "group") {
                        if (isDefaultItemRow(row)) {
                            return (
                                <Typography fontSize={12}>{row.date_time || "-"}</Typography>
                            );
                        }

                        return (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    value={row.date_time ? dayjs(row.date_time) : null}
                                    ampm={false}
                                    format="YYYY-MM-DD HH:mm:ss"
                                    timeSteps={{ minutes: 1 }}
                                    onChange={(newValue) => {
                                        updateRow(row.id, {
                                            date_time: newValue
                                                ? dayjs(newValue).format("YYYY-MM-DD HH:mm:ss")
                                                : null,
                                        });
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            variant: "outlined",
                                            onMouseDown: stopGridEvent,
                                            sx: {
                                                // width: 180,
                                                "& .MuiInputBase-input": {
                                                    fontSize: 12, // ขนาดตัวอักษร
                                                    // padding: "0px 6px", // spacing ด้านใน
                                                    height: 25,
                                                },
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        );
                    } else if (row.kind === "group") {
                        return (
                            <Typography fontSize={12}></Typography>
                        );
                    }

                    return (
                        <Typography fontSize={12}>
                            {row.date_time ?? "-"}
                        </Typography>
                    );
                },
            },
            {
                field: " ",
                headerName: "",
                width: 40,
                sortable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) =>
                    params.row.kind !== "group" && !isDefaultItemRow(params.row) && (
                        <IconButton onClick={() => {
                            handleRemove(params.row);
                        }}>
                            <DeleteIcon fontSize="small" color="action" />
                        </IconButton>
                    ),
            },
            {
                field: "result",
                headerName: "Result",
                width: 90,
                sortable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) =>
                    params.row.kind === "group" ? (
                        <IconButton
                            size="small"
                            onMouseDown={stopGridEvent}
                            onClick={() => handleAddRowToGroup(params.row)}
                        >
                            <AddCircleIcon className='text-gray-400 hover:text-gray-500' fontSize="small" />
                        </IconButton>
                    ) : (
                        <Typography
                            fontSize={12}
                            fontWeight={600}
                            color={params.row.result === "Permit" ? "green" : params.row.result === "" ? "" : "red"}
                        >
                            {params.row.result === "" ? "-" : params.row.result}
                        </Typography>
                    ),
            },
        ],
        [updateRow, filterResourceOptions, handleRemove, handleAddRowToGroup, handleToggleChecked]
    );

    useEffect(() => {
        const normalizedRows = normalizeRowsWithValidation(rows);
        const hasDiff = normalizedRows.some((row, index) => {
            const currentRow = rows[index];
            return JSON.stringify(row) !== JSON.stringify(currentRow);
        });

        if (hasDiff) {
            persistRows(normalizedRows);
            setRows(normalizedRows);
        }
    }, [rows, normalizeRowsWithValidation, persistRows, setRows]);

    useEffect(() => {
        const handleInitialOption = async () => {
            // Accessed Resource Options
            const policyFile = JSON.parse(localStorage.getItem('policy-file'));
            if (!policyFile) return;
            const rules = await getRuleByPolicyDiagram(policyFile);
            const resource_options = rules.map(rule => {
                return {
                    label: `${rule.resource.name} (${rule.resource.type})`,
                    value: rule.resource.name,
                    type: rule.resource.type
                }
            });
            setResourceOptions(resource_options);
            // Incoming Resource Options
            const incoming_resource_options = clients.map(client => {
                return {
                    label: `${client.client_name} (${client.app_type})`,
                    value: client.client_name,
                    type: client.app_type
                }
            });
            setIncomingResourceOptions(incoming_resource_options);
        };
        if (openSimulatorPolicy) {
            handleInitialOption();
        }
    }, [openSimulatorPolicy, clients])

    return (
        <Box sx={{
            height: '100%',
            width: '100%',
        }}>
            <DataGridPremium
                // apiRef={apiRef}
                rows={rows}
                columns={columns}
                hideFooter
                disableColumnMenu
                disableColumnFilter
                disableColumnSelector
                disableRowSelectionOnClick
                rowHeight={38}
                columnHeaderHeight={34}
                pinnedColumns={{
                    left: ['checked'],  // stick ซ้าย
                    right: ['result', ' '],  // stick ขวา
                }}
                getRowClassName={(params) =>
                    params.row.kind === "group" ? "groupCell" : ""
                }
                sx={{
                    // border: "1px solid #bfc7d5",
                    // borderTop: "3px solid #1e9be8",
                    fontSize: 12,

                    "& .groupCell": {
                        backgroundColor: "#e8edf9 !important",
                        fontWeight: 600,
                    },

                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#eeeeee",
                        borderBottom: "2px solid #1e9be8",
                    },

                    "& .MuiDataGrid-columnHeader": {
                        color: "#555",
                        fontWeight: 600,
                    },

                    "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #dddddd",
                        outline: "none !important",
                        display: "flex",
                        alignItems: "center",
                    },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                        outline: "none",
                    },

                    "& .groupCell .MuiDataGrid-cell": {
                        borderTop: "1px solid #c5cce0",
                        borderBottom: "1px solid #c5cce0",
                        backgroundColor: "#e8edf9 !important",
                        fontWeight: 600,
                    },

                    "& .MuiCheckbox-root": {
                        padding: 0,
                    },

                    "& .tinySelect": {
                        height: 25,
                        fontSize: 12,
                        backgroundColor: "#fff",
                    },

                    "& .tinySelect .MuiSelect-select": {
                        paddingTop: "2px",
                        paddingBottom: "2px",
                        paddingLeft: "8px",
                    },

                    "& .envSelect": {
                        minWidth: 145,
                    },

                    "& .tinyInput .MuiInputBase-root": {
                        height: 25,
                        fontSize: 12,
                        backgroundColor: "#fff",
                    },

                    "& .tinyInput input": {
                        padding: "2px 8px",
                        // width: 165,
                    },

                    "& .MuiDataGrid-pinnedColumns .groupCell .MuiDataGrid-cell": {
                        backgroundColor: "#e8edf9 !important",
                        fontWeight: 600,
                    },
                }}
            />
            <ConfirmDialogReuse
                open={openConfirmRemove}
                maxWidth={"sm"}
                content={
                    <div>
                        <Typography variant="subtitle1">Are you sure you want to remove row?</Typography>
                    </div>
                }
                actions={
                    <div className='flex flex-row gap-2'>
                        <Button variant="outlined" onClick={() => onRemove()}>OK</Button>
                        <Button variant="outlined" onClick={() => handleCancelRemove()}>Cancel</Button>
                    </div>
                }
            />
        </Box>
    );
}